import { ConfirmDialog } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToolActivation } from "@/features/tools/hooks/use-tool-activation";
import { useToolCatalog } from "@/features/tools/hooks/use-tool-catalog";
import type { AvailableTool } from "@/features/tools/types";
import { useTranslation } from "@/features/utils/i18n";
import { useEffect, useState } from "react";

interface ToolActivationSwitchProps {
  availableTool: AvailableTool;
  /** Shows « Activé » / « Désactivé » beside the switch. */
  showLabel?: boolean;
  /** Runs once the switch-off is confirmed, before the activation is removed. */
  onBeforeDeactivate?: () => void;
}

/**
 * A module on or off in one click: on activates it with its defaults, off asks
 * first — it takes effect on the agents' next calls.
 */
export function ToolActivationSwitch({
  availableTool,
  showLabel = false,
  onBeforeDeactivate,
}: ToolActivationSwitchProps) {
  const { t } = useTranslation("tools");
  const catalog = useToolCatalog();
  const { activate, deactivate, isPending } = useToolActivation();
  const [confirming, setConfirming] = useState(false);
  // What the user asked for, shown until the refetched list agrees with it,
  // so the switch does not flick back while the request is on its way.
  const [requested, setRequested] = useState<boolean | null>(null);

  const isEnabled = availableTool.clientTool?.enabled || false;
  const checked = requested ?? isEnabled;

  useEffect(() => {
    if (requested === isEnabled) setRequested(null);
  }, [isEnabled, requested]);

  const apply = async (next: boolean) => {
    setRequested(next);
    const ok = next
      ? await activate(availableTool)
      : await deactivate(availableTool);
    if (!ok) setRequested(null);
  };

  const onCheckedChange = (next: boolean) => {
    if (next) void apply(true);
    else setConfirming(true);
  };

  const id = `tool-activation-${availableTool.tool.id}`;

  return (
    // The switch can sit inside a clickable card: its clicks stay its own.
    <div
      className="flex items-center gap-2"
      onClick={(e) => e.stopPropagation()}
    >
      {showLabel && (
        <Label htmlFor={id} className="text-sm text-muted-foreground">
          {checked ? t("tools.activation.on") : t("tools.activation.off")}
        </Label>
      )}
      <Switch
        id={id}
        checked={checked}
        disabled={isPending}
        onCheckedChange={onCheckedChange}
        aria-label={t("tools.activation.toggle", {
          toolLabel: catalog.label(availableTool.tool),
        })}
      />
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t("tools.activation.confirmTitle", {
          toolLabel: catalog.label(availableTool.tool),
        })}
        description={t("tools.messages.deactivateConfirm")}
        confirmLabel={t("tools.buttons.deactivate")}
        cancelLabel={t("tools.buttons.cancel")}
        onConfirm={() => {
          onBeforeDeactivate?.();
          void apply(false);
        }}
      />
    </div>
  );
}
